<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="(showLoader && !showFilters) || (!showLoader && !applyErrorState && !getPaymentsRequestsList.length && !showFilters)"
	:showFiltersAndEmptyState="!showLoader && !applyErrorState && !getPaymentsRequestsList.length && showFilters"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    :showFloatingButton="showFilters && !applyErrorState"
    @floatingButtonClicked="showFilterModal = true"
    @backOrCloseClicked="$emit('backOrCloseClicked')">
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFiltersHandler(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
			<!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && !!getPaymentsRequestsList.length" class="content-wrapper prevent-auto-margins">
                <table-generic
				:headerData="tableHeaderData"
				:tableStyles="tableStyles"
				:tableData="tableData"
				:hasMore="getPaymentsRequestsHasMore"
				:isLoadingMore="tableLoadingMore"
				:loadingMoreText="t('payments.requests.baiPaga.loadingMoreText')"
				@loadMore="loadMoreHandler"
				@rowClicked="paymentsRequestsClickHandler($event)"
                @moreOptionsClicked="moreOptionsClicked"
                @downloadClicked="downloadClickedHandler"/>
            </div>
        </transition>
		<!-- Empty State -->
		<template #empty-state-slot>
			<empty-state-generic
			v-if="!showLoader && !applyErrorState && !getPaymentsRequestsList.length"
			:illustration="emptyStateIlustration"
			:title="emptyStateTitle"
			:description="emptyStateDescription"/>
		</template>
	</wrapper-features-templates-one-column>
    <!-- Detail Modal -->
	<detail-modal
	v-if="showDetailModal"
	:selectedPaymentsRequest="selectedPaymentsRequest"
	@redirectToPaymentsFeature="redirectToPaymentsFeature($event)"
	@cancelPaymentsRequest="showCenteredModal = true"
	@hideMe="showDetailModal = false"/>
	<!-- Filter Modal -->
	<filter-modal
	v-if="showFilterModal"
	:appliedFilters="appliedFilters"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
	:currency="getPaymentsRequestsList.currency"
	:statusList="getPaymentsRequestsStatusList"
	@hideMe="showFilterModal = false"
	@applyFilters="applyFiltersHandler($event)"/>
	<!-- Centered Modal -->
	<modal-centered-generic
	v-if="showCenteredModal"
	negative
	:title="t('payments.requests.baiPaga.centeredModal.title')"
	:text="t('payments.requests.baiPaga.centeredModal.text')"
	:leftButtonText="t('payments.requests.baiPaga.centeredModal.leftButtonText')"
	:rightButtonText="t('payments.requests.baiPaga.centeredModal.rightButtonText')"
	rightButtonIcon="check-line"
	@hideMe="showCenteredModal = false"
	@rightButtonClicked="cancelPaymentsRequest"/>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal "
	emailAction="sendMobilePaymentEmailNotification"
	smsAction="sendMobilePaymentSmsNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="sendInfoData"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- ShareLinkModal -->
    <modal-centered-share-link v-if="showShareLinkModal" :operationId="shareLinkOperationId" @hideMe="showShareLinkModal = false"/>
    <!-- Component to handle download logic-->
    <download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import DetailModal from '@/components/features/payments/requests/baiPaga/DetailModal'
import FilterModal from '@/components/features/payments/requests/baiPaga/FilterModal'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { getDateWithTime, getDate, createJSONDateStartDay, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { iconMapper, iconColorMapper } from '@/assets/scripts/mappers/paymentsRequests'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { amountFormat } from '@/assets/scripts/amounts'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { clearObject } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { createDownloadMobilePaymentsReceiptPdf } from '@/network/api/mobilePayments.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getMobilePaymentsStatusList, getMobilePaymentsList, rejectMobilePayment, clearConsultFeatureStates } = mapActions(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getPaymentsRequestsStatusList, getPaymentsRequestsList, getPaymentsRequestsHasMore } = mapGetters(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getMobileAppState } = mapGetters()

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)
const showDetailModal = ref(false)
const tableLoadingMore = ref(false)
const selectedPaymentsRequest = ref()
const showCenteredModal = ref(false)
const showFilterModal = ref(false)
const filterTags = ref([])
const showFilters = ref(false)
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showShareLinkModal = ref(false)
const sendInfoData = ref({ operationId: '' })
const shareLinkOperationId = ref(null)
const downloadLink = ref(null)

/*************************************************
*                                                *
*                  Empty States                  *
*                                                *
*************************************************/
const emptyStateIlustration = computed(() => {
    if (!filterTags.value.length) return 'emptyState/payments'

    return 'emptyState/filters'
})
const emptyStateTitle = computed(() => {
    if (!filterTags.value.length) return t('payments.requests.baiPaga.emptyState.title')

    return t('payments.requests.baiPaga.emptyState.noFiltersTitle')
})

const emptyStateDescription = computed(() => {
    if (!filterTags.value.length) return t('payments.requests.baiPaga.emptyState.description')

    return t('payments.requests.baiPaga.emptyState.noFiltersDescription')
})

/*************************************************
 *                                                *
 *             Payments Requests Data             *
 *                                                *
 *************************************************/
const redirectToPaymentsFeature = (selectedPaymentsRequest) => {
    navigateByName(routeList.PAYMENTS_REQUESTS_BAI_PAGA_PAY_NAME, { params: selectedPaymentsRequest })
}

const tableHeaderData = [
	{ label: t('payments.requests.baiPaga.tableHeaderData.merchant'), showInMobile: { groupWith: ['amount', 'date'] }, dataAttr: 'entity', rowCellType: 'title' },
	{ label: t('payments.requests.baiPaga.tableHeaderData.amount'), dataAttr: 'amount', rowCellType: 'amount' },
	{ label: t('payments.requests.baiPaga.tableHeaderData.date'), dataAttr: 'date', rowCellType: 'description' },
	{ label: t('payments.requests.baiPaga.tableHeaderData.status'), showInMobile: true, dataAttr: 'status', rowCellType: 'status' },
    { dataAttr: 'download', rowCellType: 'download' },
    { dataAttr: 'tableOptions', rowCellType: 'more-options', showInMobile: true }
]

const tableStyles = [null, { 'text-align': 'end' }, null, null, null, null]

const tableData = computed(() => {
	return getPaymentsRequestsList.value.map(payment => {
		let secondColValue

		if (payment.amount || payment.amount === 0) {
			secondColValue = { content: payment.amount, currency: payment.currency, noBalanceIcon: true }
		} else {
			secondColValue = amountFormat(payment.estimatedAmount, ACCOUNT_MAIN_CURRENCY) + t('payments.requests.baiPaga.tableData.to') + amountFormat(payment.maxAmount)
		}

		return [
			{ value: payment.id, dataAttr: 'id' },
			{ type: 'title', value: payment.merchant.friendlyName, dataAttr: 'entity' },
			{ type: 'amount', value: secondColValue, dataAttr: 'amount' },
			{
				value: {
					content: getDateWithTime(payment.creationDate),
					classes: getMobileAppState.value ? 'capitalize small secondary-color' : 'capitalize'
				},
				dataAttr: 'date'
			},
			{
				type: 'status',
				value: {
					text: payment.statusDescription || '',
					icon: iconMapper(payment.status),
					classes: iconColorMapper(payment.status)
				},
				dataAttr: 'status'
			},
            { type: 'download', dataAttr: 'download', hidden: !payment.hasReceipt },
            { type: 'more-options', value: tableMoreOptions(), dataAttr: 'tableOptions', hidden: !payment.hasReceipt }
		]
	})
})

const loadMoreHandler = () => {
	paginationParams.value.pageNumber++
	requestPaymentsRequestList()
}

const paymentsRequestsClickHandler = row => {
	requestPaymentDetail(getPaymentsRequestsList.value.find(rec => rec.id === row.rowData[0].value))
}

const requestPaymentDetail = (data) => {
	selectedPaymentsRequest.value = data
	showDetailModal.value = true
}

const tableMoreOptions = () => {
    const moreOptions = [
        {
            id: 1,
            text: t('payments.requests.baiPaga.moreOptions.sendEmail'),
            icon: 'letter',
            callbackFunction: () => { showSendEmailInfoModal.value = true }
        }, {
            id: 2,
            text: t('payments.requests.baiPaga.moreOptions.sendSms'),
            icon: 'chat',
            callbackFunction: () => { showSendSMSInfoModal.value = true }
        }, {
            id: 3,
            text: t('payments.requests.baiPaga.moreOptions.generateLink'),
            icon: 'link',
            callbackFunction: () => { showShareLinkModal.value = true }
        }
    ]

    if (getMobileAppState.value) {
        moreOptions.unshift({
            id: 4,
            text: t('payments.requests.baiPaga.moreOptions.download'),
            icon: 'download',
            callbackFunction: (rowId) => { downloadClickedHandler(rowId) }
        })
    }

    return moreOptions
}

const moreOptionsClicked = row => {
    sendInfoData.value = { operationId: row.rowId }
	shareLinkOperationId.value = getPaymentsRequestsList.value.find(obj => obj.id === row.rowId).operationId

	tableMoreOptions().find(obj => obj.id === row.optionClicked).callbackFunction(row.rowId)
}

const downloadClickedHandler = rowId => {
    downloadLink.value = createDownloadMobilePaymentsReceiptPdf(rowId)
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestStatusList = async () => {
	showLoader.value = true
	applyErrorState.value = false

	try {
		await getMobilePaymentsStatusList()
		requestPaymentsRequestList()
	} catch (error) {
		applyErrorState.value = true
		errorHandler(error.response.data, requestStatusList)
	}
}

const requestPaymentsRequestList = async () => {
	cancelAllPendingRequests()
	applyErrorState.value = false
	showFeedbackToast.value = false

	paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

	const paymentsListPayload = {
		...paginationParams.value,
		status: appliedFilters.value.status,
        beginDate: createJSONDateStartDay(appliedFilters.value.beginDate),
        endDate: createJSONDateEndDay(appliedFilters.value.endDate),
        startAmount: appliedFilters.value.startAmount,
        endAmount: appliedFilters.value.endAmount
	}

	try {
		await getMobilePaymentsList(paymentsListPayload)

        if (getPaymentsRequestsList.value.length) showFilters.value = true
		showLoader.value = false
		tableLoadingMore.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		tableLoadingMore.value = false
		errorHandler(error.response.data, requestPaymentsRequestList)
	}
}

const cancelPaymentsRequest = async () => {
	showOverlayLoader.value = true
	applyErrorState.value = false
	showDetailModal.value = false

	try {
		const res = await rejectMobilePayment(selectedPaymentsRequest.value.id)
		showOverlayLoader.value = false
		showCenteredModal.value = false
        resetPaginationParams()
		requestPaymentsRequestList()
		successHandler(res.data)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, cancelPaymentsRequest)
	}
}

/************************************************
*												*
*                   Filters                     *
*												*
************************************************/
const paginationParams = ref({
	pageNumber: 1
})

const resetPaginationParams = () => {
	paginationParams.value = {
		pageNumber: 1
	}
}

const appliedFilters = ref({
	status: '',
	beginDate: '',
	endDate: '',
	startAmount: '',
	endAmount: ''
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('payments.requests.baiPaga.filters.beginDate'),
        placeholder: t('payments.requests.baiPaga.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('payments.requests.baiPaga.filters.endDate'),
        placeholder: t('payments.requests.baiPaga.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyFiltersHandler = (filters) => {
	appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter] || appliedFilters.value[filter] === 0) {
            filterTags.value.push({
                id: filter,
                label: t(`payments.requests.baiPaga.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

	resetPaginationParams()
	requestPaymentsRequestList()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
		case 'endAmount':
		case 'startAmount':
			return amountFormat(value, ACCOUNT_MAIN_CURRENCY)
		case 'status':
			return getPaymentsRequestsStatusList.value.find(status => status.paymentStatusValue === value).paymentStatusDescription
		default: return value
	}
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    requestPaymentsRequestList()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    requestPaymentsRequestList()
}

/*************************************************
*                                                *
*                  Toast Handlers                *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*              Success/Error Handlers            *
*                                                *
*************************************************/
const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (response, retryFn) => {
	showOverlayLoader.value = false
	showLoader.value = false
    showCenteredModal.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestStatusList()

onBeforeUnmount(() => {
	clearConsultFeatureStates()
})

</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;

	width: 100%;
}
.input-elements-container {
    width: 202px;
}
</style>
